import { FC, MouseEvent, useRef, useState } from 'react';
import styled from 'styled-components';

import { Label } from '@/components/Forms';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';

const Dropdown = styled.div`
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Relative = styled.div`
  position: relative;
`;

export const DropdownSelectButton = styled(Text)`
  width: 100%;
  padding: 12px 16px;
  height: 42px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: 0.2s ease-out border-color;
  &:focus,
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  &:disabled {
    cursor: not-allowed;
    &:focus,
    &:hover {
      border-color: ${({ theme }) => theme.colors.grayscale};
    }
  }
`;

const DropdownBox = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const DropdownButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

interface Props {
  label: string;
  value?: boolean | string[] | number[];
  name: string;
  setValue: React.Dispatch<
    React.SetStateAction<{
      insurance: boolean | string[] | number[] | undefined;
    }>
  >;
}

const StyledLabel = styled(Label)`
  margin: 12px 0;
`;

const FilterBooleanDropdown: FC<Props> = ({ label, value, setValue, name }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const choices = [
    {
      label: 'Any',
      value: undefined,
    },
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ];

  const ref = useRef<HTMLButtonElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  return (
    <Dropdown>
      <Relative>
        <StyledLabel $color="black" $type="smallBold">
          {label}
        </StyledLabel>
        <DropdownSelectButton
          ref={ref}
          as="button"
          $type="smallBold"
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          {choices.find((choice) => value === choice.value)?.label ?? 'Any'}
          <svg
            width="8"
            height="6"
            viewBox="0 0 8 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
          </svg>
        </DropdownSelectButton>
        {showDropdown && (
          <DropdownBox>
            {choices.map((choice, index) => (
              <DropdownButton
                key={index}
                type="button"
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setValue((prev) => ({ ...prev, [name]: choice.value }));
                  setShowDropdown(false);
                }}
              >
                <Text $color="gray" $type="captionSemibold">
                  {choice.label}
                </Text>
              </DropdownButton>
            ))}
          </DropdownBox>
        )}
      </Relative>
    </Dropdown>
  );
};

export default FilterBooleanDropdown;
